// The sales lists' EXPORT CSV control (spec 09 §4.1 / §4.5 / §4.7; D-248,
// P3-D PR-D5 / D-289 rulings 1, 13, 14): one hook that walks the list and
// saves the file, a button for the page head, and the notes that say how the
// last export went.
//
// THE FILE IS THE LIST THE FILTERS DESCRIBE, NOT THE ROWS ON SCREEN. The walk
// reads the screen's own list endpoint with the screen's own filters and sort
// token, 200 rows a page, until the server says it is done or the 10 000-row
// cap bites (lib/csv.ts `walkCursorForExport`). Exporting the loaded pages
// would make the file depend on how far somebody scrolled — the waste ledger's
// month-end file once claimed a month and held 50 rows.
//
// A FAILED WALK SAVES NOTHING. Half a file that looks whole is worse than no
// file, so a page that fails mid-walk leaves only the failure note.
//
// No permission and no audit row (ruling 14): the file is built in the browser
// from reads the caller can already make, so a code on this button would
// enforce nothing.
//
// THE NOTES DESCRIBE THE LAST EXPORT UNDER THE CURRENT FILTERS. A changed
// filter or sort (`resetKey`) clears them, and a walk still running when the
// filters change is abandoned without saving: its file would describe filters
// that are no longer on screen.
//
// AN ABANDONED WALK IS ABORTED, NOT LEFT TO FINISH. Each run owns an
// AbortController whose signal rides every page read; a filter or sort change
// and an unmount abort it. The walk also stops at the next page boundary even
// if a read ignored the signal, so an abandoned walk reads at most the page in
// flight — never the up-to-50 pages it had left — and a reader who has left
// the screen is never handed a file. An abort is not a failure: it saves
// nothing and shows no note.
//
// THE FILE NEVER PRINTS AN ID WHERE THE SCREEN PRINTS A NAME. A filter phrase
// names what the filter bar names ("Customer: ТОО Стройэнерго"). While a name
// the phrases need is still resolving (`namesPending`), the button waits,
// disabled, rather than writing the raw id into line 2.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { facilityTodayIso } from "../../../../lib/business-dates";
import {
  csvConventionFor,
  downloadCsv,
  walkCursorForExport,
  type CursorPage,
} from "../../../../lib/csv";
import { UnauthorizedError } from "../../../../lib/problem";
import { useTenantName, useTenantSubdomain, useTenantTimezone } from "../../../../lib/role-codes";
import { MutationFailureNote } from "../mutation-failure-note";
import { useSalesPermissions } from "../hooks/permissions";
import {
  exportDocument,
  exportFilename,
  isAssignedOnlyReader,
  provenanceParts,
  type ExportList,
  type SerializedList,
} from "../lib/exports";

/** The export's observable states. `done.capped` is what the capped note
 * renders; `failed` keeps the error so the note can quote the server's title
 * and request id. */
export type SalesExportState =
  | { kind: "idle" }
  | { kind: "walking"; loaded: number }
  | { kind: "done"; exported: number; capped: boolean }
  | { kind: "failed"; error: unknown };

export interface SalesExportOptions<T> {
  readonly list: ExportList;
  /** Changes whenever the screen's filters or sort change. */
  readonly resetKey: string;
  /** One page of the SAME list read the screen makes, with its filters and the
   * effective sort token, at the given cursor and page size. `signal` is the
   * run's abort signal; pass it to the read. */
  readonly fetchPage: (
    cursor: string | undefined,
    limit: number,
    signal: AbortSignal,
  ) => Promise<CursorPage<T>>;
  readonly serialize: (rows: readonly T[]) => SerializedList;
  /** The active filters, as the screen names them ("Status: Confirmed"). */
  readonly filters: readonly string[];
  /** The effective wire sort token (`-confirmed_at`). */
  readonly order: string;
  /** True while a name a filter phrase prints (the customer chip's) is still
   * resolving; the button waits instead of writing the id. */
  readonly namesPending?: boolean;
}

export interface SalesExport {
  readonly list: ExportList;
  readonly state: SalesExportState;
  /** The filter phrases are not ready to print; the button is disabled. */
  readonly waiting: boolean;
  readonly run: () => Promise<void>;
}

export function useSalesExport<T>(options: SalesExportOptions<T>): SalesExport {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const tenantName = useTenantName();
  const subdomain = useTenantSubdomain();
  const timezone = useTenantTimezone();
  const [state, setState] = useState<SalesExportState>({ kind: "idle" });
  // Bumped on every filter/sort change; a walk that started under an older
  // generation neither reports progress nor saves.
  const generation = useRef(0);
  // The running walk's controller, so a filter change or an unmount can abort
  // its page reads.
  const inFlight = useRef<AbortController | undefined>(undefined);
  const waiting = options.namesPending === true;

  const { resetKey } = options;
  useEffect(() => {
    generation.current += 1;
    inFlight.current?.abort();
    setState({ kind: "idle" });
  }, [resetKey]);

  useEffect(
    () => () => {
      inFlight.current?.abort();
    },
    [],
  );

  const run = async (): Promise<void> => {
    const started = generation.current;
    const controller = new AbortController();
    inFlight.current?.abort();
    inFlight.current = controller;
    const current = () => generation.current === started && !controller.signal.aborted;
    setState({ kind: "walking", loaded: 0 });
    try {
      const { rows, capped } = await walkCursorForExport(
        (cursor, limit) => {
          // A read that ignored the signal must not carry the walk on.
          controller.signal.throwIfAborted();
          return options.fetchPage(cursor, limit, controller.signal);
        },
        {
          onProgress: (loaded) => {
            if (current()) setState({ kind: "walking", loaded });
          },
        },
      );
      if (!current()) return;
      const serialized = options.serialize(rows);
      const now = new Date();
      const content = exportDocument(
        serialized,
        provenanceParts(t, {
          tenantName,
          list: options.list,
          assignedOnly: isAssignedOnlyReader(perms),
          filters: options.filters,
          order: options.order,
          exportedAt: now.toISOString(),
          count: rows.length,
          capped,
          moneyOmitted: serialized.moneyOmitted,
          convention: csvConventionFor(i18n.language),
        }),
        csvConventionFor(i18n.language),
      );
      downloadCsv(
        exportFilename(options.list, subdomain, facilityTodayIso(now.getTime(), timezone)),
        content,
      );
      setState({ kind: "done", exported: rows.length, capped });
    } catch (error) {
      if (!current()) return;
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setState({ kind: "failed", error });
    } finally {
      if (inFlight.current === controller) inFlight.current = undefined;
    }
  };

  return { list: options.list, state, waiting, run };
}

/** The page-head button. The screen offers it only when the list's first page
 * has rows: an export of nothing is a control with nothing to do. Disabled
 * while a filter's name is still resolving (`waiting`). Until then the head
 * holds `SalesExportSlot` in its place (W2, PR-E4 wave 5). */
export function SalesExportButton({ exporter }: { exporter: SalesExport }) {
  const { t } = useTranslation();
  const { state } = exporter;
  return (
    <Button
      size="sm"
      data-testid={`sales-${exporter.list}-export`}
      disabled={exporter.waiting}
      busy={state.kind === "walking"}
      // The running row count, so a long walk visibly moves (Button's
      // busy-label rule: the control's own name says it is still working).
      busyLabel={
        state.kind === "walking" ? (
          <span data-testid="sales-export-walking" data-loaded={state.loaded}>
            {t("sales.export.walking", { count: state.loaded })}
          </span>
        ) : undefined
      }
      onClick={() => {
        void exporter.run();
      }}
    >
      {t("sales.export.button")}
    </Button>
  );
}

/** What the last export under these filters came to: the capped note when the
 * file stopped at the cap, the failure note when nothing was saved. */
export function SalesExportNotes({ exporter }: { exporter: SalesExport }) {
  const { t } = useTranslation();
  const { state } = exporter;
  if (state.kind === "done" && state.capped) {
    return (
      <p className="sales-banner" role="status" data-testid="sales-export-capped">
        {t("sales.export.cappedNote", { count: state.exported })}
      </p>
    );
  }
  if (state.kind === "failed") {
    return (
      <MutationFailureNote
        text={t("sales.export.failed")}
        error={state.error}
        data-testid="sales-export-failed"
      />
    );
  }
  return null;
}

/**
 * Export CSV's place in the page head while the book has no rows (W2, PR-E4
 * wave 5; spec 09 §4.1, design-system §5). The button is offered only once
 * the first page has rows, and it sits first in the toolbar, so without a
 * stand-in the head would grow by one control when the rows arrived and move
 * everything after it. The slot is the same Button with the same label and
 * size, so it takes the same width, and it is inert and invisible: hidden
 * from assistive technology, out of the tab order, disabled, and painted
 * with `visibility: hidden`. A screen renders it exactly where the button
 * goes, for every reader it would later offer the button to. It carries no
 * test id (design-system §5), so a test or a walkthrough that asks for the
 * export control can only ever find the live one.
 */
export function SalesExportSlot() {
  const { t } = useTranslation();
  return (
    <Button size="sm" className="sales-export-slot" aria-hidden="true" tabIndex={-1} disabled>
      {t("sales.export.button")}
    </Button>
  );
}
